import EwaybillDTO from "@/dto/order/EwaybillDTO";
import OrderDTO from "@/dto/order/OrderDTO";
import ShippingAddressDTO from "@/dto/ShippingAddressDTO";
import OrderStatus from "@/enum/OrderStatus";
import EwaybillResultStore from "@/scenes/OrderManagement/components/dialogs/PrintEwaybillResultDialog/EwaybillResultStore";
import { EwaybillGenerateResult } from "@/services/api/ewaybillGenerate";
import { WaybillPrintResultStatus } from "@/services/print/waybill/impl/AbstractEwaybillPrinter";
import { isAnyOrderItemSelected, selectAllAvailableOrderItem, sortEwaybills } from "@/utils/OrderUtils";
import { cloneDeep, uniq, uniqBy } from "lodash";
import { computed, observable, makeObservable, actionBound } from "mobx";
import { MultiShopSelectPlatformType } from "@/components/MultiShopSelect/types";
import { platforms } from "../MultiPlatformPrintWaybillModal";
import GlobalStore from "@/store";

export interface EwaybillTableColumnData {
    rowKey: string;
    orderId: number;
    shopId: number;
    printerName: string;
    ewaybill?: EwaybillDTO;
    errorMsg?: string;
    canSelected?: boolean;
}

function buildRequestErrorRowKey(orderId: number) {
    return `${orderId}_request_error`;
}

function buildGenerateErrorRowKey(orderId: number, index: number) {
    return `${orderId}_generate_error_${index}`;
}

function buildEwaybillRowKey(orderId: number, ewaybill: EwaybillDTO) {
    return `${orderId}_ewaybill_${ewaybill.id}`;
}

interface InitMultiPlatformEwaybillResultStoreParams {
    useMaxQuantityPerPackage: boolean;
    copies: number;
    shippingAddress: ShippingAddressDTO;
    isGenerateWaybillNoOnly: boolean;
    processInRange?: boolean;
    listOrders: OrderDTO[];
    orderPlatformMap: Map<number, MultiShopSelectPlatformType>;
}

export default class MultiPlatformEwaybillResultStore {
    constructor(public globalStore: typeof GlobalStore) {
        makeObservable(this, {
            copies: observable,
            useMaxQuantityPerPackage: observable,
            shippingAddress: observable,
            isGenerateWaybillNoOnly: observable,
            printerNames: computed,
            dataSource: computed,
            requestGenerateErrorResultMapping: computed,
            waybillNoPrintSuccessMapping: computed,
            waybillNoPrintErrorsMapping: computed,
            totalDocumentCount: computed,
            printIndexMapping: computed,
            selectedRowKeys: observable,
            onRowSelectionChange: actionBound,
            orders: computed,
            orderMapping: computed,
            generateSuccessCount: computed,
            generateErrorCount: computed,
            printErrorCount: computed,
            printSuccessCount: computed,
            waitingSendCount: computed,
            showShip: computed,
            waybillNoGenerateResults: computed,
            waybillNoGenerateResultMapping: computed,
            ewaybillMapping: computed,
            selectAllOrder: actionBound,
            getSelectedOrders: actionBound,
            handleRePrint: actionBound,
        });
    }

    init(params: InitMultiPlatformEwaybillResultStoreParams) {
        this.copies = params.copies;
        this.useMaxQuantityPerPackage = params.useMaxQuantityPerPackage;
        this.shippingAddress = params.shippingAddress;
        this.isGenerateWaybillNoOnly = params.isGenerateWaybillNoOnly;
        this.processInRange = params.processInRange;
        this.listOrders = params.listOrders;
        this.orderPlatformMap = params.orderPlatformMap;
    }

    [MultiShopSelectPlatformType.tb]: EwaybillResultStore;

    [MultiShopSelectPlatformType.pdd]: EwaybillResultStore;

    [MultiShopSelectPlatformType.dy]: EwaybillResultStore;

    [MultiShopSelectPlatformType.ks]: EwaybillResultStore;

    [MultiShopSelectPlatformType.jd]: EwaybillResultStore;

    [MultiShopSelectPlatformType.ali]: EwaybillResultStore;

    [MultiShopSelectPlatformType.channelShop]: EwaybillResultStore;

    [MultiShopSelectPlatformType.xhs]: EwaybillResultStore;

    [MultiShopSelectPlatformType.wb]: EwaybillResultStore;

    [MultiShopSelectPlatformType.other]: EwaybillResultStore;

    [MultiShopSelectPlatformType.other_drop_shipping_supplier_ks]: EwaybillResultStore;
    [MultiShopSelectPlatformType.other_drop_shipping_supplier_xhs]: EwaybillResultStore;
    [MultiShopSelectPlatformType.other_drop_shipping_supplier_channel_shop]: EwaybillResultStore;
    [MultiShopSelectPlatformType.other_drop_shipping_supplier_wb]: EwaybillResultStore;
    [MultiShopSelectPlatformType.other_drop_shipping_supplier_other]: EwaybillResultStore;

    [MultiShopSelectPlatformType.ali_out_order_taobao]: EwaybillResultStore;
    [MultiShopSelectPlatformType.ali_out_order_pdd]: EwaybillResultStore;
    [MultiShopSelectPlatformType.ali_out_order_dy]: EwaybillResultStore;
    [MultiShopSelectPlatformType.ali_out_order_kwai]: EwaybillResultStore;
    [MultiShopSelectPlatformType.ali_out_order_xhs]: EwaybillResultStore;
    [MultiShopSelectPlatformType.ali_out_order_jd]: EwaybillResultStore;
    [MultiShopSelectPlatformType.ali_out_order_weixin]: EwaybillResultStore;

    [MultiShopSelectPlatformType.ali_plaintext_out_order_taobao]: EwaybillResultStore;
    [MultiShopSelectPlatformType.ali_plaintext_out_order_pdd]: EwaybillResultStore;
    [MultiShopSelectPlatformType.ali_plaintext_out_order_dy]: EwaybillResultStore;
    [MultiShopSelectPlatformType.ali_plaintext_out_order_kwai]: EwaybillResultStore;
    [MultiShopSelectPlatformType.ali_plaintext_out_order_xhs]: EwaybillResultStore;
    [MultiShopSelectPlatformType.ali_plaintext_out_order_jd]: EwaybillResultStore;

    copies: number;

    useMaxQuantityPerPackage: boolean;

    shippingAddress: ShippingAddressDTO;

    isGenerateWaybillNoOnly: boolean;

    processInRange: boolean;

    listOrders: OrderDTO[] = [];

    orderPlatformMap: Map<number, MultiShopSelectPlatformType> = new Map();

    /**
     * 按照打印机分组
     */
    get printerNames() {
        const printerNames: string[] = [];

        for (let i = 0; i < platforms.length; i++) {
            const platform = platforms[i];

            if (!this[platform]) {
                continue;
            }

            if (this[platform].printerName) {
                printerNames.push(this[platform].printerName);
            }
        }

        return uniq(printerNames);
    }

    get platformStoresMapping() {
        const mapping = new Map<string, EwaybillResultStore[]>();

        for (let i = 0; i < this.printerNames.length; i++) {
            const printerName = this.printerNames[i];

            const stores = this.getPlatformStoresByPrinterName(printerName);

            mapping.set(printerName, stores);
        }

        return mapping;
    }

    getPlatformStoresByPrinterName(printerName: string) {
        const stores: EwaybillResultStore[] = [];
        for (let i = 0; i < platforms.length; i++) {
            const platform = platforms[i];

            if (!this[platform]) {
                continue;
            }

            if (this[platform].printerName === printerName) {
                stores.push(this[platform]);
            }
        }

        return stores;
    }

    get dataSource() {
        // 获取单号全部失败，如打印份数为2时，该订单获取2个单号全部失败
        const allFailure: EwaybillTableColumnData[] = [];

        // 获取单号部分成功，如打印份数为2时，该订单获取1个单号成功1个单号失败（如：余额不足，第1个获取成功，第2个刚好余额不足）
        const partSuccess: EwaybillTableColumnData[] = [];

        // 获取单号全部成功，如打印份数为2时，该订单获取2个单号全部成功
        const allSuccess: EwaybillTableColumnData[] = [];

        if (this.processInRange) {
            for (const order of this.listOrders) {
                const platform = this.orderPlatformMap.get(order.id);

                const store = this[platform];

                if (!store) {
                    continue;
                }

                const result = store.waybillNoGenerateResultMapping.get(order.id);

                if (!result) {
                    const errorMsg = store.requestGenerateErrorResultMapping.get(order.id);

                    allFailure.push({
                        rowKey: buildRequestErrorRowKey(order.id),
                        orderId: order.id,
                        errorMsg: errorMsg,
                        shopId: order.shopId,
                        printerName: store.printerName,
                    });
                    continue;
                }

                const ewaybills = result.ewaybills;

                const errorList = result.errors.map((error, index) => {
                    return {
                        rowKey: buildGenerateErrorRowKey(result.orderId, index),
                        orderId: result.orderId,
                        errorMsg: error,
                        shopId: order.shopId,
                        printerName: store.printerName,
                    };
                });

                const successList = ewaybills.map((ewaybill, index) => {
                    // 这里是因为是以ewaybill为一行,但是发货又是以order维度去发货,多运单号,选第一个即可发货
                    const canSelected = index === 0;

                    return {
                        rowKey: buildEwaybillRowKey(result.orderId, ewaybill),
                        orderId: result.orderId,
                        ewaybill,
                        errorMsg: undefined,
                        shopId: order.shopId,
                        printerName: store.printerName,
                        canSelected,
                    };
                });

                if (result.errors.length === 0) {
                    allSuccess.push(...successList);
                } else if (ewaybills.length === 0) {
                    allFailure.push(...errorList);
                } else {
                    partSuccess.push(...successList, ...errorList);
                }
            }
        } else {
            for (let i = 0; i < platforms.length; i++) {
                const platform = platforms[i];

                const store = this[platform];

                if (!store) {
                    continue;
                }

                store.orders.forEach((order) => {
                    const result = store.waybillNoGenerateResultMapping.get(order.id);

                    if (!result) {
                        const errorMsg = store.requestGenerateErrorResultMapping.get(order.id);

                        allFailure.push({
                            rowKey: buildRequestErrorRowKey(order.id),
                            orderId: order.id,
                            errorMsg: errorMsg,
                            shopId: order.shopId,
                            printerName: store.printerName,
                        });
                        return;
                    }

                    const ewaybills = result.ewaybills;

                    const errorList = result.errors.map((error, index) => {
                        return {
                            rowKey: buildGenerateErrorRowKey(result.orderId, index),
                            orderId: result.orderId,
                            errorMsg: error,
                            shopId: order.shopId,
                            printerName: store.printerName,
                        };
                    });

                    const successList = ewaybills.map((ewaybill, index) => {
                        // 这里是因为是以ewaybill为一行,但是发货又是以order维度去发货,多运单号,选第一个即可发货
                        const canSelected = index === 0;

                        return {
                            rowKey: buildEwaybillRowKey(result.orderId, ewaybill),
                            orderId: result.orderId,
                            ewaybill,
                            errorMsg: undefined,
                            shopId: order.shopId,
                            printerName: store.printerName,
                            canSelected,
                        };
                    });

                    if (result.errors.length === 0) {
                        allSuccess.push(...successList);
                    } else if (ewaybills.length === 0) {
                        allFailure.push(...errorList);
                    } else {
                        partSuccess.push(...successList, ...errorList);
                    }
                });
            }
        }

        return [...allFailure, ...partSuccess, ...allSuccess];
    }

    get requestGenerateErrorResultMapping() {
        const map: Map<number, string> = new Map();

        for (let i = 0; i < platforms.length; i++) {
            const platform = platforms[i];

            if (this[platform]?.requestGenerateErrorResultMapping) {
                this[platform].requestGenerateErrorResultMapping.forEach((value, key) => {
                    map.set(key, value);
                });
            }
        }

        return map;
    }

    /**
     * 打印发生成功的运单
     */
    get waybillNoPrintSuccessMapping() {
        const map: Map<string, string> = new Map();

        for (let i = 0; i < platforms.length; i++) {
            const platform = platforms[i];

            if (this[platform]?.waybillPrintResults) {
                this[platform].waybillPrintResults.forEach((printResult) => {
                    if (printResult.status === WaybillPrintResultStatus.SUCCESS) {
                        map.set(printResult.waybillNo, printResult.message || "打印成功");
                    }
                });
            }
        }

        return map;
    }

    /**
     * 打印发生错误的运单
     */
    get waybillNoPrintErrorsMapping() {
        const map: Map<string, string> = new Map();

        for (let i = 0; i < platforms.length; i++) {
            const platform = platforms[i];

            if (this[platform]?.waybillPrintResults) {
                this[platform].waybillPrintResults.forEach((printResult) => {
                    if (printResult.status === WaybillPrintResultStatus.FAIL) {
                        map.set(printResult.waybillNo, printResult.message || "打印失败");
                    }
                });
            }
        }

        return map;
    }

    get totalDocumentCount() {
        /** 每个平台的totalDocumentCount是一致的 */

        for (let i = 0; i < platforms.length; i++) {
            const platform = platforms[i];

            if (this[platform]?.totalDocumentCount) {
                return this[platform].totalDocumentCount;
            }
        }
    }

    get printIndexMapping() {
        const map: Map<string, string> = new Map();

        for (let i = 0; i < platforms.length; i++) {
            const platform = platforms[i];

            if (this[platform]?.printIndexMapping) {
                this[platform].printIndexMapping.forEach((value, key) => {
                    map.set(key, value);
                });
            }
        }

        return map;
    }

    selectedRowKeys: string[] = [];

    onRowSelectionChange(selectedRowKeys: string[]) {
        this.selectedRowKeys = selectedRowKeys;
    }

    get orders() {
        const orders: OrderDTO[] = [];

        for (let i = 0; i < platforms.length; i++) {
            const platform = platforms[i];

            if (this[platform]?.orders) {
                orders.push(...this[platform].orders);
            }
        }

        return orders;
    }

    get orderMapping() {
        const mapping = new Map<number, OrderDTO>();

        this.orders.forEach((order) => {
            mapping.set(order.id, order);
        });

        return mapping;
    }

    get generateSuccessCount() {
        let count = 0;

        for (let i = 0; i < platforms.length; i++) {
            const platform = platforms[i];

            if (this[platform]?.generateSuccessCount) {
                count += this[platform]?.generateSuccessCount;
            }
        }

        return count;
    }

    get generateErrorCount() {
        let count = 0;

        for (let i = 0; i < platforms.length; i++) {
            const platform = platforms[i];

            if (this[platform]?.generateErrorCount) {
                count += this[platform]?.generateErrorCount;
            }
        }

        return count;
    }

    get printErrorCount() {
        let count = 0;

        for (let i = 0; i < platforms.length; i++) {
            const platform = platforms[i];

            if (this[platform]?.printErrorCount) {
                count += this[platform]?.printErrorCount;
            }
        }

        return count;
    }

    get printSuccessCount() {
        let count = 0;

        for (let i = 0; i < platforms.length; i++) {
            const platform = platforms[i];

            if (this[platform]?.printSuccessCount) {
                count += this[platform]?.printSuccessCount;
            }
        }

        return count;
    }

    get waitingSendCount() {
        return this.generateSuccessCount - this.printSuccessCount - this.printErrorCount;
    }

    get showShip() {
        return !this.orders.some((order) => {
            return [
                OrderStatus.SUCCESS,
                OrderStatus.CANCELED,
                OrderStatus.SHIPPED,
                OrderStatus.PENDING_PAYMENT,
            ].includes(order.status);
        });
    }

    get waybillNoGenerateResults() {
        const waybillNoGenerateResults: EwaybillGenerateResult[] = [];

        for (let i = 0; i < platforms.length; i++) {
            const platform = platforms[i];

            if (this[platform]?.waybillNoGenerateResults) {
                waybillNoGenerateResults.push(...this[platform].waybillNoGenerateResults);
            }
        }

        return waybillNoGenerateResults;
    }

    get waybillNoGenerateResultMapping() {
        const mapping = new Map<number, EwaybillGenerateResult>();

        this.waybillNoGenerateResults.forEach((result) => {
            mapping.set(result.orderId, result);
        });

        return mapping;
    }

    get ewaybillMapping() {
        const mapping = new Map<number, EwaybillDTO[]>();

        this.waybillNoGenerateResults.forEach((result) => {
            mapping.set(result.orderId, result?.ewaybills || []);
        });

        return mapping;
    }

    selectAllOrder() {
        const dataSource = this.dataSource;
        const selectedRowKeys: string[] = [];

        for (let i = 0; i < dataSource.length; i++) {
            const data = dataSource[i];
            const orderId = data.orderId;

            const waybillNoGenerateResult = this.waybillNoGenerateResultMapping.get(orderId);

            if (data.ewaybill && !waybillNoGenerateResult?.errors?.length) {
                selectedRowKeys.push(data.rowKey);
            }
        }

        this.selectedRowKeys = selectedRowKeys;
    }

    getSelectedOrders(): OrderDTO[] {
        const orderIds: number[] = [];
        const dataSourceMapping = new Map(this.dataSource.map((data) => [data.rowKey, data]));

        this.selectedRowKeys.forEach((rowKey) => {
            const data = dataSourceMapping.get(rowKey);

            if (!data) {
                return;
            }

            orderIds.push(data.orderId);
        });

        const uniqOrderIds = uniq(orderIds);

        const selectedOrders: OrderDTO[] = [];

        uniqOrderIds.forEach((orderId) => {
            const order = this.orderMapping.get(orderId);

            if (!order) {
                return;
            }

            const waybillNoGenerateResult = this.waybillNoGenerateResultMapping.get(orderId);

            if (!waybillNoGenerateResult?.ewaybills?.length) {
                return;
            }

            // 本次打印的运单号
            const ewaybills: EwaybillDTO[] = [...(waybillNoGenerateResult.ewaybills || [])];
            ewaybills.reverse(); // 倒序，最后生成的单号排在前面

            const mainEwaybill = ewaybills[0];
            const waybillNo = mainEwaybill.parentWaybillNo || mainEwaybill.waybillNo;

            let newEwaybills: EwaybillDTO[] = [...(order.ewaybills || [])];
            newEwaybills.push(...ewaybills);
            newEwaybills = uniqBy(newEwaybills, (it) => it.id);

            sortEwaybills(newEwaybills, waybillNo);

            const newOrder = cloneDeep(order);
            newOrder.ewaybills = newEwaybills;
            newOrder.logisticsCompany = mainEwaybill.logisticsCompany;
            newOrder.waybillNo = waybillNo;

            // 如果用户没有部分勾选则默认全选所有有效的商品打印
            if (!isAnyOrderItemSelected(newOrder)) {
                selectAllAvailableOrderItem(newOrder);
            }

            selectedOrders.push(newOrder);
        });

        return selectedOrders;
    }

    handleRePrint(ewaybill: EwaybillDTO) {
        for (let i = 0; i < platforms.length; i++) {
            const platform = platforms[i];

            const hasFindEwaybill = !!this[platform]?.waybillNoGenerateResults.find((it) =>
                it.ewaybills?.find((it) => it.id === ewaybill.id),
            );
            if (hasFindEwaybill) {
                return this[platform].handleRePrint(ewaybill, this[platform].template);
            }
        }
    }
}
